import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import {
  listContactApprovals,
  approveContactApproval,
  denyContactApproval,
  ContactApproval,
  ContactApprovalStatus,
} from '../services/api';

const STATUS_FILTERS: Array<{ value: ContactApprovalStatus | 'all'; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'applied', label: 'Applied' },
  { value: 'denied', label: 'Denied' },
  { value: 'apply-failed', label: 'Apply failed' },
  { value: 'all', label: 'All' },
];

function fmtWhen(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function fmtValue(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

function StatusPill({ status }: { status: ContactApprovalStatus }) {
  const cls: Record<ContactApprovalStatus, string> = {
    pending: 'bg-amber-100 text-amber-800 border-amber-200',
    applied: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    denied: 'bg-slate-100 text-slate-600 border-slate-200',
    'apply-failed': 'bg-rose-100 text-rose-800 border-rose-200',
  };
  const label = status === 'apply-failed' ? 'apply-failed' : status;
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${cls[status]}`}>{label}</span>
  );
}

export default function ContactApprovalsAdmin() {
  const [items, setItems] = useState<ContactApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ContactApprovalStatus | 'all'>('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [denyingId, setDenyingId] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState('');

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await listContactApprovals(filter === 'all' ? undefined : filter);
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(id: string) {
    setActingId(id);
    try {
      await approveContactApproval(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setActingId(null);
    }
  }

  async function handleDeny(id: string) {
    setActingId(id);
    try {
      await denyContactApproval(id, denyReason.trim() || undefined);
      setDenyingId(null);
      setDenyReason('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deny failed');
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="max-w-6xl mx-auto py-4">
      <PageHeader />
      <div className="mt-4">
        <h1 className="text-2xl font-semibold text-slate-900">Contact Approvals</h1>
        <p className="text-sm text-slate-500 mt-1">Yumi's crowdsourced write-backs awaiting review.</p>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1 text-sm rounded border ${
              filter === f.value
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
        <button onClick={() => void load()} className="ml-auto px-3 py-1 text-sm rounded border border-slate-300 hover:bg-slate-50">
          Refresh
        </button>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded bg-rose-50 border border-rose-200 text-rose-800 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="mt-6 text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-6 text-sm text-slate-500">No approvals in this view.</div>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((a) => {
            const isOpen = expandedId === a.id;
            const isPending = a.status === 'pending';
            return (
              <div key={a.id} className="border border-slate-200 rounded bg-white">
                <button
                  onClick={() => setExpandedId(isOpen ? null : a.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
                >
                  <StatusPill status={a.status} />
                  <span className="text-xs uppercase text-slate-500 tracking-wide">{a.kind}</span>
                  <span className="text-sm font-medium text-slate-900">
                    {a.kind === 'overwrite'
                      ? `Contact #${a.contactId} — ${a.changes?.length || 0} field${(a.changes?.length || 0) === 1 ? '' : 's'} changing`
                      : `${a.newContact?.firstName || ''} ${a.newContact?.lastName || ''}`.trim() || 'New contact'}
                  </span>
                  <span className="text-xs text-slate-500 ml-auto">
                    {a.requestedBy} · {fmtWhen(a.requestedAt)}
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-200 p-4 space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div><span className="text-slate-500">Company ID:</span> {a.companyId}</div>
                      {a.supportSessionCode && (
                        <div><span className="text-slate-500">Support code:</span> {a.supportSessionCode}</div>
                      )}
                    </div>

                    {a.callerAffirmation && (
                      <div className="p-3 bg-indigo-50 border border-indigo-200 rounded">
                        <div className="text-xs uppercase tracking-wide text-indigo-700 mb-1">Caller affirmation</div>
                        <div className="italic text-slate-800">"{a.callerAffirmation}"</div>
                      </div>
                    )}

                    {a.kind === 'overwrite' && a.changes && a.changes.length > 0 && (
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="text-xs uppercase text-slate-500 border-b border-slate-200">
                            <th className="text-left py-2">Field</th>
                            <th className="text-left py-2">Current</th>
                            <th className="text-left py-2">Proposed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {a.changes.map((c) => (
                            <tr key={c.field} className="border-b border-slate-100">
                              <td className="py-2 font-medium">{c.field}</td>
                              <td className="py-2 text-slate-600">{fmtValue(c.oldValue)}</td>
                              <td className="py-2 text-slate-900 font-medium">{fmtValue(c.newValue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {a.kind === 'create' && a.newContact && (
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded text-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div><span className="text-slate-500">First name:</span> {a.newContact.firstName}</div>
                          <div><span className="text-slate-500">Last name:</span> {a.newContact.lastName}</div>
                          {a.newContact.emailAddress && <div><span className="text-slate-500">Email:</span> {a.newContact.emailAddress}</div>}
                          {a.newContact.phone && <div><span className="text-slate-500">Phone:</span> {a.newContact.phone}</div>}
                          {a.newContact.mobilePhone && <div><span className="text-slate-500">Mobile:</span> {a.newContact.mobilePhone}</div>}
                          {a.newContact.title && <div><span className="text-slate-500">Title:</span> {a.newContact.title}</div>}
                        </div>
                      </div>
                    )}

                    {a.applyError && (
                      <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800">
                        Apply error: {a.applyError}
                      </div>
                    )}
                    {a.denialReason && (
                      <div className="p-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700">
                        Denied: {a.denialReason} {a.reviewedBy ? `(by ${a.reviewedBy})` : ''}
                      </div>
                    )}
                    {a.appliedAt && (
                      <div className="text-xs text-slate-500">
                        Applied {fmtWhen(a.appliedAt)} {a.reviewedBy ? `by ${a.reviewedBy}` : ''}
                        {a.appliedContactId ? ` — new contact #${a.appliedContactId}` : ''}
                      </div>
                    )}

                    {isPending && (
                      <div className="flex flex-wrap items-center gap-2 pt-2">
                        <button
                          onClick={() => void handleApprove(a.id)}
                          disabled={actingId === a.id}
                          className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {actingId === a.id ? 'Applying…' : 'Approve & apply'}
                        </button>
                        <button
                          onClick={() => { setDenyingId(a.id); setDenyReason(''); }}
                          disabled={actingId === a.id}
                          className="px-3 py-1.5 text-sm rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                        >
                          Deny
                        </button>
                        {denyingId === a.id && (
                          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                            <input
                              type="text"
                              value={denyReason}
                              onChange={(e) => setDenyReason(e.target.value)}
                              placeholder="Optional reason"
                              className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded"
                              autoFocus
                            />
                            <button
                              onClick={() => void handleDeny(a.id)}
                              disabled={actingId === a.id}
                              className="px-3 py-1.5 text-sm rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                            >
                              Confirm deny
                            </button>
                            <button
                              onClick={() => { setDenyingId(null); setDenyReason(''); }}
                              className="px-3 py-1.5 text-sm rounded bg-white text-slate-600 border border-slate-300 hover:bg-slate-50"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
